/**
 * LagRootCauseAnalyzer — synthesizes signals from the existing diagnostic
 * stack into ONE actionable sentence describing the most likely lag cause.
 *
 * Phase H.8 (2026-05-12). DJ previously had to look at four panels
 * (LatencyOverlay, NetSpikeAnalyzer, EventRateOverlay tick budget,
 * EventRateOverlay payload sizes) and correlate manually. This module
 * runs the correlation: server tick over-budget vs client snap rate vs
 * network spike tier vs oversized payload. Whichever signal is loudest
 * gets the verdict.
 *
 * Reads (all read-only — no side effects):
 *   - `window.__HELIX_MP_DIAGNOSTICS__.phaseTimings` (LatencyReporter snapshot)
 *   - Most recent `netobs:tick` (TickHealthSnapshot) — must be cached by caller
 *   - `window.nm.tracer.{outbound,inbound}MessageTypeStats` — payload sizes
 *
 * Designed to be polled every ~500ms by the LagRootCauseOverlay debug
 * tool. Cost is a fixed-size scan over a handful of small maps.
 */

import type { TickHealthSnapshot } from '../../shared/diagnostics/TickHealthTypes';

/** LRG.2 (2026-05-13) — Message types that are inherently diagnostic /
 *  presentation chatter, not gameplay traffic. A single 31KB
 *  `networkDiagnostics` response on a debug panel open is NOT user-visible
 *  lag — but the prior heuristic flagged it SEVERE and shadowed every
 *  other signal. Excluding these types from the oversized-payload check
 *  keeps the verdict honest. Order: most-common-first for early-exit. */
const DIAGNOSTIC_MESSAGE_TYPES: ReadonlySet<string> = new Set([
  'networkDiagnostics',
  'netobs:tick',
  'netobs:eventRate',
  'netobs:latency_aggregate',
  'syncProbeAck',
  'serverDraining',
  'statusApplied',
  'statusRemoved',
  'statusTick',
]);

/** Stale-data window. A maxBytes seen 10s+ ago is most likely a
 *  one-shot historical spike, not a current gameplay lag source. */
const OVERSIZED_PAYLOAD_STALENESS_MS = 10_000;

/** Minimum recent occurrences for a message type to count toward the
 *  oversized-payload heuristic. A truly one-shot oversized blob (e.g. a
 *  diagnostic response, a one-time level-load broadcast) should not
 *  generate a SEVERE verdict. */
const OVERSIZED_PAYLOAD_MIN_COUNT = 3;

export type LagSeverity = 'healthy' | 'mild' | 'severe' | 'unknown';

export interface LagDiagnosis {
  /** Top-line single sentence — what to put in the UI banner. */
  summary: string;
  /** Severity for UI color coding. */
  severity: LagSeverity;
  /** Which category triggered the verdict — useful for telemetry. */
  category: 'server_tick' | 'network_spike' | 'oversized_payload' | 'healthy' | 'unknown';
  /** Underlying numbers for follow-up reads (panel can show details). */
  details: Record<string, number | string | undefined>;
  /**
   * How many of the three inputs actually reported this pass. The verdict is
   * only worth what this number says (DEBUG-LAGROOT-INPUTS-01, 2026-07-27):
   * `healthy` is the FALL-THROUGH of the heuristic, so before this field it
   * was returned identically whether all three inputs were read and clean or
   * none of them existed. The 2026-07-22 devkit audit recorded a bare
   * "🟢 Healthy" as "the verdict is backed by live inputs" — it had no way to
   * check, because the tool did not say. See docs/architecture/measurement-parity.md.
   */
  inputsAvailable: number;
}

/** The three inputs, and whether each reported. Merged into every `details`. */
interface InputAvailability extends Record<string, string> {
  in_tickHealth: string;
  in_spikeTier: string;
  in_payloadStats: string;
}

export interface AnalyzerInputs {
  /** Most recent server tick health snapshot (null if not yet received). */
  tickHealth: TickHealthSnapshot | null;
  /** NetSpikeAnalyzer current-tier string, or undefined if not warmed/observed. */
  spikeTier?: string;
}

type TracerStat = { count: number; maxBytes?: number; lastAt?: string };
type Tracer = {
  outboundMessageTypeStats?: Map<string, TracerStat>;
  inboundMessageTypeStats?: Map<string, TracerStat>;
};

/** Hoisted out of heuristic step 3 so availability is known before step 1 runs. */
function readTracer(): Tracer | undefined {
  try {
    return (globalThis as unknown as { nm?: { tracer?: Tracer } }).nm?.tracer;
  } catch {
    return undefined;
  }
}

/**
 * Run the heuristic. The rules are intentionally simple — order is by
 * "most likely to feel laggy", first-match wins. Returns a single verdict.
 */
export function analyzeLag(inputs: AnalyzerInputs): LagDiagnosis {
  // Resolve input availability BEFORE the heuristic, so an early-returning
  // branch still reports what it could and could not see.
  const tracer = readTracer();
  const availability: InputAvailability = {
    in_tickHealth: inputs.tickHealth ? 'live' : 'absent (no netobs:tick received yet)',
    in_spikeTier: inputs.spikeTier
      ? `live (${inputs.spikeTier})`
      : 'absent (Spike Analyzer window not open — the tier is read off its DOM node)',
    in_payloadStats: tracer ? 'live' : 'absent (window.nm.tracer not warm)',
  };
  const inputsAvailable =
    (inputs.tickHealth ? 1 : 0) + (inputs.spikeTier ? 1 : 0) + (tracer ? 1 : 0);

  // 1. Server tick over budget — every client feels this immediately.
  if (inputs.tickHealth) {
    const { maxMs, budgetMs, subsystems } = inputs.tickHealth;
    if (maxMs > budgetMs) {
      // Find worst subsystem by mean.
      let worst: { name: string; meanMs: number } | null = null;
      if (subsystems) {
        for (const [name, s] of Object.entries(subsystems)) {
          if (!worst || s.meanMs > worst.meanMs) worst = { name, meanMs: s.meanMs };
        }
      }
      const sub = worst ? ` — ${worst.name} took ${worst.meanMs.toFixed(1)}ms mean` : '';
      return {
        summary: `Server tick over budget: max ${maxMs.toFixed(1)}ms vs ${budgetMs.toFixed(1)}ms${sub}`,
        severity: maxMs > budgetMs * 2 ? 'severe' : 'mild',
        category: 'server_tick',
        details: {
          ...availability,
          maxMs,
          budgetMs,
          worstSubsystem: worst?.name,
          worstSubsystemMeanMs: worst?.meanMs,
        },
        inputsAvailable,
      };
    }
  }

  // 2. Network spike tier — sustained burst above baseline.
  if (inputs.spikeTier === 'SEVERE' || inputs.spikeTier === 'CRITICAL') {
    return {
      summary: `Network spike: traffic at ${inputs.spikeTier} tier (>5× rolling baseline)`,
      severity: inputs.spikeTier === 'CRITICAL' ? 'severe' : 'mild',
      category: 'network_spike',
      details: { ...availability, spikeTier: inputs.spikeTier },
      inputsAvailable,
    };
  }

  // 3. Oversized payloads — surfaces fat broadcasts that compound under
  //    load. LRG.2 (2026-05-13): three new guards prevent false-positive
  //    SEVERE verdicts on diagnostic chatter:
  //    a) Skip message types in DIAGNOSTIC_MESSAGE_TYPES (intrinsically
  //       large but one-shot; not gameplay-blocking).
  //    b) Skip message types whose `lastAt` is older than 10s (the
  //       maxBytes value is stale; the offender hasn't recently fired).
  //    c) Require count >= OVERSIZED_PAYLOAD_MIN_COUNT (truly one-shot
  //       blobs don't represent sustained gameplay lag).
  try {
    if (tracer) {
      const nowMs = Date.now();
      const findFattest = (m?: Map<string, TracerStat>) => {
        if (!m) return null;
        let worst: { type: string; maxBytes: number; count: number } | null = null;
        for (const [t, s] of m.entries()) {
          if (DIAGNOSTIC_MESSAGE_TYPES.has(t)) continue;
          const mb = s.maxBytes ?? 0;
          const count = s.count ?? 0;
          if (mb <= 1024) continue;
          if (count < OVERSIZED_PAYLOAD_MIN_COUNT) continue;
          if (s.lastAt) {
            const lastMs = new Date(s.lastAt).getTime();
            if (Number.isFinite(lastMs) && nowMs - lastMs > OVERSIZED_PAYLOAD_STALENESS_MS) continue;
          }
          if (!worst || mb > worst.maxBytes) worst = { type: t, maxBytes: mb, count };
        }
        return worst;
      };
      const fattest = findFattest(tracer.outboundMessageTypeStats) || findFattest(tracer.inboundMessageTypeStats);
      if (fattest) {
        return {
          summary: `Oversized payload: ${fattest.type} reached ${fattest.maxBytes}B (count=${fattest.count})`,
          severity: fattest.maxBytes > 4096 ? 'severe' : 'mild',
          category: 'oversized_payload',
          details: { ...availability, messageType: fattest.type, maxBytes: fattest.maxBytes, count: fattest.count },
          inputsAvailable,
        };
      }
    }
  } catch { /* tracer not warm */ }

  // 4. Nothing red — but "nothing red" and "nothing looked at" reach this
  //    same line, so say which one happened. Zero inputs is not health; it is
  //    the absence of a measurement, and this tool is read as a health signal.
  if (inputsAvailable === 0) {
    return {
      summary: 'No verdict — none of the 3 lag inputs are reporting',
      severity: 'unknown',
      category: 'unknown',
      details: availability,
      inputsAvailable,
    };
  }
  return {
    summary: `Healthy — no dominant lag source detected (${inputsAvailable}/3 inputs read)`,
    severity: 'healthy',
    category: 'healthy',
    details: availability,
    inputsAvailable,
  };
}
